// ─────────────────────────────────────────────────────────────────────────────
// auth/deviceLogin.js — "Sign in with a code from your phone" (backend PLAN.md)
//
// The computer asks for a request (a short code to show, a long secret to keep),
// then polls with the secret until the phone approves or denies. The phone
// looks the code up, shows what is asking, and approves: the server wants a
// passkey check in the last 60 seconds for that, and authClient's "Confirm
// it's you" dialog provides it when asked (403 reauth_required).
//
// The QR code holds studio.fennira.com/link#CODE: after "#", so the code never
// reaches a server log or a Referer header. Never the secret.
// ─────────────────────────────────────────────────────────────────────────────

import axios from "axios";
import { authHeader } from "../authClient";

const withAuth = () => ({ headers: authHeader() });

/** "K7M-42Q" → "https://studio.fennira.com/link#K7M42Q" */
export const linkUrl = (userCode) => `${window.location.origin}/link#${String(userCode).replace(/-/g, "")}`;

/** The code from a /link#K7M42Q address, or "". */
export function codeFromLocation(loc = window.location) {
  const raw = decodeURIComponent(String(loc.hash || "").replace(/^#/, ""));
  return /^[2-9A-HJKMNP-Z]{6}$/i.test(raw) ? `${raw.slice(0, 3)}-${raw.slice(3)}`.toUpperCase() : "";
}

/** "k7m42q" while typing → "K7M-42Q" */
export function formatCode(input) {
  const s = String(input || "").toUpperCase().replace(/[^0-9A-Z]/g, "").slice(0, 6);
  return s.length > 3 ? `${s.slice(0, 3)}-${s.slice(3)}` : s;
}

// ── The computer ─────────────────────────────────────────────────────────────

/** mode "login" (signed out) or "reauth" (confirm this signed-in browser). */
export async function startRequest(mode = "login") {
  const { data } = mode === "reauth"
    ? await axios.post("/api/auth/device/reauth/start", {}, { ...withAuth(), skipReauth: true })
    : await axios.post("/api/auth/device/start", {});
  return data;   // { requestSecret, userCode, expiresIn, interval }
}

export async function pollRequest(requestSecret) {
  const { data } = await axios.post("/api/auth/device/poll", { requestSecret }, { __noAuthRedirect: true });
  return data;   // { status, ...tokens when a sign-in is approved }
}

// ── The phone ────────────────────────────────────────────────────────────────

export async function lookupCode(code) {
  const { data } = await axios.post("/api/auth/device/lookup", { code }, withAuth());
  return data;
}

export async function approveCode(code) {
  const { data } = await axios.post("/api/auth/device/approve", { code }, withAuth());
  return data;
}

export async function denyCode(code) {
  const { data } = await axios.post("/api/auth/device/deny", { code }, withAuth());
  return data;
}

/** A server error as a sentence. */
export function explainCodeError(err, fallback = "That didn't work. Please try again.") {
  const d = err?.response?.data;
  if (err?.response?.status === 429 && !d?.code) return "Too many tries. Please wait a moment.";
  if (d?.message) return d.message;
  if (!err?.response && err?.request) return "No connection. Check your internet and try again.";
  return fallback;
}

// ── Coming back to /link after signing in on the phone ───────────────────────

const AFTER_LOGIN_KEY = "fennira.afterLogin";

/** Remember to return to the approval page (and its code) after signing in. */
export function rememberLinkPage(code) {
  try { sessionStorage.setItem(AFTER_LOGIN_KEY, code ? `/link#${String(code).replace(/-/g, "")}` : "/link"); } catch { /* private mode */ }
}

/** Where to go after a sign-in: the approval page if it sent us, else null. Read once. */
export function takeAfterLoginPath() {
  try {
    const p = sessionStorage.getItem(AFTER_LOGIN_KEY);
    sessionStorage.removeItem(AFTER_LOGIN_KEY);
    return p && /^\/link(#[0-9A-Z]{6})?$/.test(p) ? p : null;
  } catch { return null; }
}
